/**
 * Binding a booking to the rotation it is actually on.
 *
 * A booking bucket key and a flight row's `bookingKey` are both
 * `<carrier><number>_<origin>_<destination>_<YYYY-MM-DD>`, and the timeline used to pair
 * them by comparing that string. The date on each side is derived from a different field,
 * and neither field is stable:
 *
 *   - flight document / row: the scheduled ARRIVAL day at the airport (the DAA feed builds
 *     flightDate from OperationalTimes.ScheduledDateTime; prm-flights timeline-utils keys
 *     DUB rows off it)
 *   - booking: the live `arrivalTime` day, or the original arrival day once a delay passes
 *     the 10h threshold in prm-bookings formatFlightDictionary, or `departureDate` when
 *     there is no arrival time at all
 *
 * For an overnight arrival the two anchors are legitimately a day apart — AA724 CLT->DUB
 * departs Charlotte on the 15th and lands in Dublin on the 16th, so the airline, the
 * passenger's ticket and SITA all call it "15 Sep" while the flight document is "16 Sep".
 * Add a second rotation of the same flight number in the same window (the previous day's
 * AA724, delayed and cancelled) and the date string cannot tell the two apart: the 16 Sep
 * passenger binds to the 15 Sep row, which never flew, and has no assignable card.
 *
 * Reported at DUB on 16 Sep 2026. The same collision was fixed on the SLA side in
 * prm-cpu `buildInboundBookingsByFlightId`, and this is that invariant applied to the
 * timeline: every booking binds to EXACTLY ONE rotation — the one whose own time is
 * nearest the booking's. Nearest-wins, never a tolerance test, so no passenger is ever
 * dropped off the board.
 */

const BOARDING_SUFFIX = '_BOARDING';
const BUCKET_DATE = /^\d{4}-\d{2}-\d{2}$/;

const EMPTY_BOOKINGS = Object.freeze([]);

/**
 * Split `AA724_CLT_DUB_2026-09-16` into the rotation prefix and its date.
 * Returns null for anything that is not a dated flight key.
 */
export const parseFlightBucketKey = (key) => {
    if (typeof key !== 'string') return null;
    const idx = key.lastIndexOf('_');
    if (idx === -1) return null;
    const date = key.slice(idx + 1);
    if (!BUCKET_DATE.test(date)) return null;
    return { prefix: key.slice(0, idx), date };
};

const toMs = (value) => {
    if (!value) return null;
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
};

const firstTime = (...values) => {
    for (const value of values) {
        const ms = toMs(value);
        if (ms !== null) return ms;
    }
    return null;
};

// The row's own clock: the live operation time the timeline slots it by, falling back to
// the schedule. For departures timeline-utils already puts the STD in operationTime.
const rowTimeMs = (flight) => firstTime(
    flight?.operationTime,
    flight?.operationInfo?.time,
    flight?.scheduled
);

const isDepartureRow = (flight) => (
    flight?.type === 'departure' ||
    flight?.type === 'boarding' ||
    flight?.isBoardingFlight === true
);

// The booking's own clock, read from whichever shape the doc is in (arrival docs keep
// flightConfirmationDetails at the top level and the untouched original underneath).
const bookingTimeMs = (booking, departure) => {
    const fcd = booking?.flightConfirmationDetails
        ?? booking?.originalBookingDoc?.flightConfirmationDetails
        ?? booking?.flightDetails;
    if (!fcd) return null;

    return departure
        ? firstTime(fcd.departureTime, fcd.departureDate)
        : firstTime(fcd.arrivalTime, fcd.departureTime, fcd.departureDate);
};

// Rotations of one flight number + route, earliest first, so ties resolve the same way
// on every render.
const groupRowsByRotation = (flightsByKey) => {
    const rowsByRotation = {};
    if (!flightsByKey) return rowsByRotation;

    for (const rowKey in flightsByKey) {
        if (rowKey.endsWith(BOARDING_SUFFIX)) continue;
        const parsed = parseFlightBucketKey(rowKey);
        if (!parsed) continue;

        const flight = flightsByKey[rowKey];
        if (!rowsByRotation[parsed.prefix]) rowsByRotation[parsed.prefix] = [];
        rowsByRotation[parsed.prefix].push({
            key: rowKey,
            date: parsed.date,
            time: rowTimeMs(flight),
            departure: isDepartureRow(flight),
        });
    }

    for (const prefix in rowsByRotation) {
        rowsByRotation[prefix].sort((a, b) => (
            a.date === b.date ? (a.key < b.key ? -1 : 1) : (a.date < b.date ? -1 : 1)
        ));
    }

    return rowsByRotation;
};

// The rotation closest to the booking's own time. A booking with no usable time keeps the
// row its date already pointed at, and failing that the earliest rotation — it still lands
// somewhere assignable.
const nearestRow = (rows, timeMs, bucketKey) => {
    const dateMatch = rows.find((row) => row.key === bucketKey);

    if (timeMs === null) return dateMatch ?? rows[0];

    let best = null;
    for (const row of rows) {
        if (row.time === null) continue;
        const gap = Math.abs(row.time - timeMs);
        if (!best || gap < best.gap || (gap === best.gap && row.key < best.key)) {
            best = { key: row.key, gap };
        }
    }

    return best ?? dateMatch ?? rows[0];
};

/**
 * Re-key every booking bucket onto the flight row it belongs to.
 *
 * @param {Object} bookingsDict - bucket key -> bookings, as prm-bookings grouped them
 * @param {Object} flightsByKey - flight row bookingKey -> flight row
 * @returns {Object} flight row bookingKey -> bookings bound to that row
 */
export const resolveBookingBuckets = (bookingsDict, flightsByKey) => {
    const resolved = {};
    if (!bookingsDict) return resolved;

    const bind = (key, bookings) => {
        if (!key || !Array.isArray(bookings) || bookings.length === 0) return;
        if (!resolved[key]) resolved[key] = [];
        for (const booking of bookings) resolved[key].push(booking);
    };

    const rowsByRotation = groupRowsByRotation(flightsByKey);

    for (const bucketKey in bookingsDict) {
        const bookings = bookingsDict[bucketKey];
        if (!Array.isArray(bookings) || bookings.length === 0) continue;

        // boarding combos are keyed off the booking doc, not a flight day
        if (bucketKey.endsWith(BOARDING_SUFFIX)) {
            bind(bucketKey, bookings);
            continue;
        }

        const parsed = parseFlightBucketKey(bucketKey);
        const rows = parsed ? rowsByRotation[parsed.prefix] : null;

        // no row for this flight at all — leave the bucket where it is, so nothing is lost
        // if the flight feed later catches up
        if (!rows || rows.length === 0) {
            bind(bucketKey, bookings);
            continue;
        }

        // one rotation on the board owns every booking for that flight, whichever day each
        // side keyed. This is the overnight departure/arrival anchor split.
        if (rows.length === 1) {
            bind(rows[0].key, bookings);
            continue;
        }

        // several rotations of the same flight number: each booking joins the one nearest
        // its own time
        for (const booking of bookings) {
            bind(nearestRow(rows, bookingTimeMs(booking, rows[0].departure), bucketKey).key, [booking]);
        }
    }

    return resolved;
};

export const bookingsForFlight = (resolved, flightKey) => (
    (flightKey && resolved?.[flightKey]) || EMPTY_BOOKINGS
);
